/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Button,
  Card,
  Icon,
  Loader,
  removeScaleFromMoney,
  Table,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsync, useAsyncFn } from 'react-use';
import {
  exportTransactionsCsv,
  searchTransactions,
} from 'components/Transactions/api';
import { getWalletDetails } from 'components/Wallets/api';

export const WalletDetails = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { walletId } = useParams();
  if (!walletId) {
    throw Error('Missing walletId param');
  }

  const { value: wallet, loading: walletLoading } = useAsync(
    () => getWalletDetails(walletId),
    [walletId]
  );

  const { value: txResponse, loading: txLoading } = useAsync(
    () =>
      searchTransactions({
        filters: { walletId },
        pagination: { page: 1, size: 100 },
      }),
    [walletId]
  );

  const [{ loading: exportLoading }, exportCsvFn] = useAsyncFn(async () => {
    const blob = await exportTransactionsCsv({ walletId });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-${walletId}-transactions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [walletId]);

  const txHeaders = [
    { label: 'Created At', cellKey: 'createdAt', sort: false },
    { label: 'ID', cellKey: 'id', sort: false },
    { label: 'Type', cellKey: 'type', sort: false },
    { label: 'Amount', cellKey: 'amount', sort: false },
    { label: 'Status', cellKey: 'status', sort: false },
  ];

  const txRows = useMemo(() => {
    if (!txResponse) return [];
    return txResponse.transactions.map(
      ({ id, amount, type, createdAt, status }) => ({
        value: {
          createdAt: new Date(createdAt).toLocaleString(),
          id: <TruncatedString text={id} />,
          uuid: id,
          type,
          status,
          amount: `${removeScaleFromMoney(amount)} ${amount.currencyCode}`,
        },
      })
    );
  }, [txResponse]);

  if (walletLoading || !wallet) {
    return <Loader />;
  }

  return (
    <>
      <Header>
        <div css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}>
          <div
            css={{
              cursor: 'pointer',
              ':hover': {
                textDecoration: 'underline',
                textDecorationColor: palette.grey.x300,
              },
            }}
            onClick={() => navigate('/wallets')}
          >
            <Typography variant="subtitle" color={palette.grey.x300}>
              Wallets
            </Typography>
          </div>
          <div
            css={{
              transform: 'rotate(180deg)',
              svg: { fill: 'none', stroke: palette.grey.x300 },
            }}
          >
            <Icon name="chevron" />
          </div>
          <Typography variant="subtitle">{walletId}</Typography>
        </div>
      </Header>

      <div css={{ display: 'grid', gap: spacing.xxs }}>
        <Card>
          <div css={{ display: 'grid', gap: spacing.xxxs }}>
            <Typography variant="subtitle">Wallet</Typography>
            <Row label="Wallet ID" value={wallet.uuid} />
            <Row label="Address" value={wallet.address ?? '—'} />
            <Row label="Currency" value={wallet.currencyCode} />
            <Row label="Type" value={wallet.walletType} />
            <Row
              label="Balance"
              value={`${removeScaleFromMoney(wallet.balance)} ${
                wallet.balance.currencyCode
              }`}
            />
            <Row label="Disabled" value={wallet.disabled ? 'Yes' : 'No'} />
            <Row
              label="Nexus account code"
              value={wallet.nexusAccountCode ?? '—'}
            />
          </div>
        </Card>

        <Card>
          <div css={{ display: 'grid', gap: spacing.xxxs }}>
            <Typography variant="subtitle">User</Typography>
            <Row label="User ID" value={wallet.userId} />
            <Row label="Email" value={wallet.userEmail ?? '—'} />
            <div>
              <Button
                variant="text"
                text="Open user"
                onClick={() => navigate(`/users/app/${wallet.userId}`)}
              />
            </div>
          </div>
        </Card>

        <Card>
          <div css={{ display: 'grid', gap: spacing.xxxs }}>
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle">Transactions</Typography>
              <UtilityButton
                variant="secondary"
                text="Export CSV"
                loading={exportLoading}
                disabled={exportLoading}
                onClick={exportCsvFn}
              />
            </div>
            {txLoading ? (
              <Loader />
            ) : (
              <Table
                headers={txHeaders}
                rows={txRows}
                // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
                onRowClick={(row) => navigate(`/transactions/${row.uuid}`)}
              />
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => {
  const { spacing } = useTheme();
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '200px 1fr',
        gap: spacing.xxs,
      }}
    >
      <Typography variant="body">{label}</Typography>
      <Typography variant="body">{value}</Typography>
    </div>
  );
};
